import { inject, Injectable } from '@angular/core';
import { LoadingService } from './loading.service';
import { ticketTranslations } from '../translations/modules/tickets.translations';

export const languages = {
  English: 'en',
  French: 'fr',
  Arabic: 'ar',
} as const;

export type language = (typeof languages)[keyof typeof languages];

@Injectable({
  providedIn: 'root',
})
export class DictionaryService {
  loadingService = inject(LoadingService);

  language: language = this.initLanguage();
  dictionary: Record<string, Record<language, string>> = {};

  initLanguage(): language {
    const storedLanguage = window.localStorage.getItem('language') as language;

    if (Object.values(languages).includes(storedLanguage)) {
      this.language = storedLanguage;
    } else {
      this.language = languages.English;
      window.localStorage.setItem('language', this.language);
    }
    return this.language;
  }

  constructor() {
    this.dictionary = {
      Dashboard: { en: 'Dashboard', fr: 'Tableau de bord', ar: 'لوحة القيادة' },
      Users: { en: 'Users', fr: 'Utilisateurs', ar: 'المستخدمون' },
      Supervisors: {
        en: 'Supervisors',
        fr: 'Superviseurs',
        ar: 'المشرفون',
      },
      PlanDrafts: {
        en: 'Plan Drafts',
        fr: 'Brouillons de plan',
        ar: 'مسودات الخطة',
      },
      LoadingPlanDrafts: {
        en: 'Loading plan drafts...',
        fr: 'Chargement des brouillons de plan...',
        ar: 'جاري تحميل مسودات الخطة...',
      },
      NoPlanDraftsFound: {
        en: 'No plan drafts found',
        fr: 'Aucun brouillon de plan trouvé',
        ar: 'لم يتم العثور على مسودات خطة',
      },
      FailedToLoadPlanDrafts: {
        en: 'Failed to load plan drafts',
        fr: 'Échec du chargement des brouillons de plan',
        ar: 'فشل تحميل مسودات الخطة',
      },
      FailedToApprovePlanDraft: {
        en: 'Failed to approve plan draft',
        fr: "Échec de l'approbation du brouillon de plan",
        ar: 'فشل الموافقة على مسودة الخطة',
      },
      FailedToRejectPlanDraft: {
        en: 'Failed to reject plan draft',
        fr: 'Échec du rejet du brouillon de plan',
        ar: 'فشل رفض مسودة الخطة',
      },
      RejectPlanDraft: {
        en: 'Reject Plan Draft',
        fr: 'Rejeter le brouillon de plan',
        ar: 'رفض مسودة الخطة',
      },
      RejectionMessage: {
        en: 'Rejection Message',
        fr: 'Message de rejet',
        ar: 'رسالة الرفض',
      },
      EnterRejectionReason: {
        en: 'Enter rejection reason',
        fr: 'Entrer la raison du rejet',
        ar: 'أدخل سبب الرفض',
      },
      PleaseEnterRejectionMessage: {
        en: 'Please enter a rejection message',
        fr: 'Veuillez entrer un message de rejet',
        ar: 'الرجاء إدخال رسالة الرفض',
      },
      PlanDraftApprovedSuccessfully: {
        en: 'Plan draft approved successfully',
        fr: 'Brouillon de plan approuvé avec succès',
        ar: 'تمت الموافقة على مسودة الخطة بنجاح',
      },
      PlanDraftRejectedSuccessfully: {
        en: 'Plan draft rejected successfully',
        fr: 'Brouillon de plan rejeté avec succès',
        ar: 'تم رفض مسودة الخطة بنجاح',
      },
      Products: { en: 'Products', fr: 'Produits', ar: 'المنتجات' },
      Bricks: { en: 'Bricks', fr: 'Briques', ar: 'المناطق' },
      Customers: { en: 'Customers', fr: 'Clients', ar: 'العملاء' },
      IdBrowser: {
        en: 'ID Browser',
        fr: "Navigateur d'ID",
        ar: 'متصفح الهوية',
      },
      Evaluations: { en: 'Evaluations', fr: 'Évaluations', ar: 'التقييمات' },
      Reports: { en: 'Reports', fr: 'Rapports', ar: 'التقارير' },
      Email: { en: 'Email', fr: 'E-mail', ar: 'البريد الإلكتروني' },
      Intelligence: { en: 'Intelligence', fr: 'Intelligence', ar: 'الذكاء' },
      Samples: { en: 'Samples', fr: 'Échantillons', ar: 'العينات' },
      SampleDetails: {
        en: 'Sample Details',
        fr: 'Détails des Échantillons',
        ar: 'تفاصيل العينات',
      },
      Regions: { en: 'Regions', fr: 'Régions', ar: 'المناطق' },
      Promoters: { en: 'Promoters', fr: 'Promoteurs', ar: 'المروجون' },
      UserAnalytics: {
        en: 'User Analytics',
        fr: 'Analyse des utilisateurs',
        ar: 'تحليلات المستخدمين',
      },
      CatalogueAnalytics: {
        en: 'Catalogue Analytics',
        fr: 'Analyse des catalogues',
        ar: 'تحليلات الكتالوجات',
      },
      ProductAnalytics: {
        en: 'Product Analytics',
        fr: 'Analyse des produits',
        ar: 'تحليلات المنتجات',
      },
      LeaveManagement: {
        en: 'Leave Management',
        fr: 'Gestion des congés',
        ar: 'إدارة الإجازات',
      },
      CustomerService: {
        en: 'Customer Service',
        fr: 'Service client',
        ar: 'خدمة العملاء',
      },
      Notifications: {
        en: 'Notifications',
        fr: 'Notifications',
        ar: 'الإشعارات',
      },

      Sales: { en: 'Sales', fr: 'Ventes', ar: 'المبيعات' },
      SalesSheets: {
        en: 'Sales Sheets',
        fr: 'Feuilles de ventes',
        ar: 'ملفات المبيعات',
      },
      SalesSheetsDescription: {
        en: 'Upload distributor sales sheets and follow their status.',
        fr: "Importez les feuilles de ventes des distributeurs et suivez l'etat de leur correspondance de schema.",
        ar: 'ارفع ملفات مبيعات الموزعين وتابع حالة الملفات.',
      },
      UploadSalesSheet: {
        en: 'Upload Sales Sheet',
        fr: 'Importer une feuille de ventes',
        ar: 'رفع ملف مبيعات',
      },
      UploadSalesSheetDescription: {
        en: 'Choose the distributor and sheet type, then upload one Excel or CSV file for processing.',
        fr: 'Choisissez le distributeur et le type de feuille, puis importez un fichier Excel ou CSV a traiter.',
        ar: 'اختر الموزع ونوع الملف، ثم ارفع ملف Excel أو CSV واحداً للمعالجة.',
      },
      Distributor: {
        en: 'Distributor',
        fr: 'Distributeur',
        ar: 'الموزع',
      },
      SelectDistributor: {
        en: 'Select a distributor',
        fr: 'Selectionner un distributeur',
        ar: 'اختر موزعاً',
      },
      DistributorRequired: {
        en: 'Distributor is required.',
        fr: 'Le distributeur est requis.',
        ar: 'اختيار الموزع مطلوب.',
      },
      LoadingDistributors: {
        en: 'Loading distributors...',
        fr: 'Chargement des distributeurs...',
        ar: 'جارٍ تحميل الموزعين...',
      },
      DistributorsLoadFailed: {
        en: 'Failed to load distributors.',
        fr: 'Echec du chargement des distributeurs.',
        ar: 'تعذر تحميل الموزعين.',
      },
      NoDistributorsFound: {
        en: 'No distributors are available.',
        fr: "Aucun distributeur n'est disponible.",
        ar: 'لا يوجد موزعون متاحون.',
      },
      SalesSheetType: {
        en: 'Sheet Type',
        fr: 'Type de feuille',
        ar: 'نوع الملف',
      },
      SelectSalesSheetType: {
        en: 'Select a sheet type',
        fr: 'Selectionner un type de feuille',
        ar: 'اختر نوع الملف',
      },
      SalesSheetTypeRequired: {
        en: 'Sheet type is required.',
        fr: 'Le type de feuille est requis.',
        ar: 'نوع الملف مطلوب.',
      },
      SalesSheetFile: {
        en: 'Sales Sheet File',
        fr: 'Fichier de ventes',
        ar: 'ملف المبيعات',
      },
      ChooseSalesSheetFile: {
        en: 'Choose a file',
        fr: 'Choisir un fichier',
        ar: 'اختر ملفاً',
      },
      SalesSheetFileHint: {
        en: 'XLSX, XLS, or CSV up to 10 MB',
        fr: "XLSX, XLS ou CSV jusqu'a 10 Mo",
        ar: 'XLSX أو XLS أو CSV بحد أقصى 10 ميجابايت',
      },
      SalesSheetFileRequired: {
        en: 'A sales sheet file is required.',
        fr: 'Un fichier de ventes est requis.',
        ar: 'ملف المبيعات مطلوب.',
      },
      SalesSheetUnsupportedFile: {
        en: 'Only XLSX, XLS, and CSV files are supported.',
        fr: 'Seuls les fichiers XLSX, XLS et CSV sont pris en charge.',
        ar: 'الملفات المدعومة هي XLSX وXLS وCSV فقط.',
      },
      SalesSheetFileTooLarge: {
        en: 'The file must not exceed 10 MB.',
        fr: 'Le fichier ne doit pas depasser 10 Mo.',
        ar: 'يجب ألا يتجاوز حجم الملف 10 ميجابايت.',
      },
      SalesSheetUploadFailed: {
        en: 'Failed to upload the sales sheet.',
        fr: "Echec de l'importation de la feuille de ventes.",
        ar: 'تعذر رفع ملف المبيعات.',
      },
      SalesSheetPathMissing: {
        en: 'This sales sheet does not have a file to preview.',
        fr: "Cette feuille de ventes n'a aucun fichier a afficher.",
        ar: 'لا يحتوي ملف المبيعات هذا على ملف للمعاينة.',
      },
      InvalidRows: {
        en: 'Invalid Rows',
        fr: 'Lignes invalides',
        ar: 'الصفوف غير الصالحة',
      },
      View: {
        en: 'View',
        fr: 'Afficher',
        ar: 'عرض',
      },
      ViewInvalidRows: {
        en: 'View invalid rows',
        fr: 'Afficher les lignes invalides',
        ar: 'عرض الصفوف غير الصالحة',
      },
      SalesSheetInvalidRowsLoadFailed: {
        en: 'Failed to load invalid rows.',
        fr: 'Échec du chargement des lignes invalides.',
        ar: 'تعذر تحميل الصفوف غير الصالحة.',
      },
      SalesSheetInvalidRowsUnavailable: {
        en: 'Invalid rows are unavailable for this sales sheet.',
        fr: 'Les lignes invalides ne sont pas disponibles pour cette feuille de ventes.',
        ar: 'الصفوف غير الصالحة غير متاحة لملف المبيعات هذا.',
      },
      SalesSheetStatusPending: {
        en: 'Pending',
        fr: 'En attente',
        ar: 'قيد الانتظار',
      },
      SalesSheetStatusProcessing: {
        en: 'Processing',
        fr: 'Traitement',
        ar: 'قيد المعالجة',
      },
      SalesSheetStatusDone: {
        en: 'Done',
        fr: 'Terminé',
        ar: 'مكتمل',
      },
      SalesSheetStatusInvalidData: {
        en: 'Invalid Sheet Data',
        fr: 'Données de feuille invalides',
        ar: 'بيانات الملف غير صالحة',
      },
      SalesSheetStatusFailed: {
        en: 'Failed',
        fr: 'Échec',
        ar: 'فشل',
      },
      SalesSheetStatusUnknown: {
        en: 'Unknown status',
        fr: 'Statut inconnu',
        ar: 'حالة غير معروفة',
      },
      SalesSheetProgress: {
        en: 'Sales sheet progress',
        fr: 'Progression de la feuille de ventes',
        ar: 'تقدم ملف المبيعات',
      },
      SalesSheetStageCompleted: {
        en: 'completed',
        fr: 'terminé',
        ar: 'مكتمل',
      },
      SalesSheetStageCurrent: {
        en: 'current',
        fr: 'actuel',
        ar: 'الحالي',
      },
      SalesSheetStageUpcoming: {
        en: 'upcoming',
        fr: 'à venir',
        ar: 'قادم',
      },
      Refresh: { en: 'Refresh', fr: 'Actualiser', ar: 'تحديث' },
      Upload: { en: 'Upload', fr: 'Importer', ar: 'رفع' },
      Uploading: {
        en: 'Uploading...',
        fr: 'Importation...',
        ar: 'جارٍ الرفع...',
      },

      CreateNotification: {
        en: 'Create Notification',
        fr: 'Creer une notification',
        ar: 'إنشاء إشعار',
      },
      CreateNotificationHint: {
        en: 'Send a notification to all users, selected teams, or selected individuals.',
        fr: 'Envoyez une notification a tous les utilisateurs, a des equipes ou a des personnes selectionnees.',
        ar: 'إرسال إشعار إلى كل المستخدمين أو فرق محددة أو أفراد محددين.',
      },
      LoadingNotificationsData: {
        en: 'Loading notification data...',
        fr: 'Chargement des donnees de notification...',
        ar: 'جاري تحميل بيانات الإشعارات...',
      },
      NotificationsLoadError: {
        en: 'Failed to load notification data',
        fr: 'Echec du chargement des donnees de notification',
        ar: 'فشل تحميل بيانات الإشعارات',
      },
      NotificationTitle: {
        en: 'Title',
        fr: 'Titre',
        ar: 'العنوان',
      },
      EnterNotificationTitle: {
        en: 'Enter notification title',
        fr: 'Entrer le titre de la notification',
        ar: 'أدخل عنوان الإشعار',
      },
      NotificationTitleMissing: {
        en: 'Notification title is required.',
        fr: 'Le titre de la notification est requis.',
        ar: 'عنوان الإشعار مطلوب.',
      },
      NotificationBody: {
        en: 'Message',
        fr: 'Message',
        ar: 'الرسالة',
      },
      EnterNotificationBody: {
        en: 'Enter notification message',
        fr: 'Entrer le message de la notification',
        ar: 'أدخل رسالة الإشعار',
      },
      NotificationBodyMissing: {
        en: 'Notification message is required.',
        fr: 'Le message de la notification est requis.',
        ar: 'رسالة الإشعار مطلوبة.',
      },
      Application: {
        en: 'Application',
        fr: 'Application',
        ar: 'التطبيق',
      },
      SelectApplication: {
        en: 'Select application',
        fr: "Selectionner l'application",
        ar: 'اختر التطبيق',
      },
      ApplicationMissing: {
        en: 'Application is required.',
        fr: "L'application est requise.",
        ar: 'التطبيق مطلوب.',
      },
      NotificationType: {
        en: 'Notification Type',
        fr: 'Type de notification',
        ar: 'نوع الإشعار',
      },
      SelectNotificationType: {
        en: 'Select notification type',
        fr: 'Selectionner le type de notification',
        ar: 'اختر نوع الإشعار',
      },
      NotificationTypeMissing: {
        en: 'Notification type is required.',
        fr: 'Le type de notification est requis.',
        ar: 'نوع الإشعار مطلوب.',
      },
      SelectSupervisors: {
        en: 'Select supervisors',
        fr: 'Selectionner les superviseurs',
        ar: 'اختر المشرفين',
      },
      SelectUsers: {
        en: 'Select users',
        fr: 'Selectionner les utilisateurs',
        ar: 'اختر المستخدمين',
      },
      NotificationRecipientsMissing: {
        en: 'Please select at least one recipient.',
        fr: 'Veuillez selectionner au moins un destinataire.',
        ar: 'يرجى اختيار مستلم واحد على الأقل.',
      },
      SendingNotification: {
        en: 'Sending...',
        fr: 'Envoi...',
        ar: 'جاري الإرسال...',
      },
      NotificationCreatedSuccess: {
        en: 'Notification created successfully',
        fr: 'Notification creee avec succes',
        ar: 'تم إنشاء الإشعار بنجاح',
      },
      NotificationCreateFailed: {
        en: 'Failed to create notification',
        fr: 'Echec de la creation de la notification',
        ar: 'فشل إنشاء الإشعار',
      },

      NotificationHelpTitle: {
        en: 'Need help?',
        fr: "Besoin d'aide ?",
        ar: 'تحتاج مساعدة؟',
      },
      NotificationHelpSubtitle: {
        en: 'Learn more about notifications',
        fr: 'En savoir plus sur les notifications',
        ar: 'اعرف المزيد عن الإشعارات',
      },
      NotificationHelpModalTitle: {
        en: 'Notification Types',
        fr: 'Types de notification',
        ar: 'أنواع الإشعارات',
      },
      NotificationHelpModalIntro: {
        en: 'Choose the notification type based on who should receive the message.',
        fr: 'Choisissez le type de notification selon les personnes qui doivent recevoir le message.',
        ar: 'اختر نوع الإشعار بناءً على الأشخاص الذين يجب أن تصل إليهم الرسالة.',
      },
      NotificationHelpAllTitle: {
        en: 'All',
        fr: 'Tous',
        ar: 'الكل',
      },
      NotificationHelpAllDescription: {
        en: 'Sends the notification to every active user in the selected application.',
        fr: "Envoie la notification a tous les utilisateurs actifs de l'application selectionnee.",
        ar: 'يرسل الإشعار إلى كل المستخدمين النشطين في التطبيق المحدد.',
      },
      NotificationHelpTeamTitle: {
        en: 'Team',
        fr: 'Equipe',
        ar: 'الفريق',
      },
      NotificationHelpTeamDescription: {
        en: 'Sends the notification to users under the selected supervisor teams.',
        fr: 'Envoie la notification aux utilisateurs des equipes de superviseurs selectionnees.',
        ar: 'يرسل الإشعار إلى المستخدمين ضمن فرق المشرفين المحددة.',
      },
      NotificationHelpIndividualsTitle: {
        en: 'Individuals',
        fr: 'Individus',
        ar: 'أفراد',
      },
      NotificationHelpIndividualsDescription: {
        en: 'Sends the notification only to the selected individual users.',
        fr: 'Envoie la notification uniquement aux utilisateurs selectionnes.',
        ar: 'يرسل الإشعار فقط إلى المستخدمين الذين تم اختيارهم.',
      },

      VisitsPerformance: {
        en: 'Visits Performance',
        fr: 'Performance des visites',
        ar: 'أداء الزيارات',
      },
      Visits: { en: 'Visits', fr: 'Visites', ar: 'الزيارات' },
      RepsPerformance: {
        en: 'Reps Performance',
        fr: 'Performance des représentants',
        ar: 'أداء المندوبين',
      },
      SalesPerformance: {
        en: 'Sales Performance',
        fr: 'Performance des ventes',
        ar: 'أداء المبيعات',
      },
      ProductsPerformance: {
        en: 'Products Performance',
        fr: 'Performance des produits',
        ar: 'أداء المنتجات',
      },
      SupervisorsPerformance: {
        en: 'Supervisors Performance',
        fr: 'Performance des superviseurs',
        ar: 'أداء المشرفين',
      },
      CallsDuration: {
        en: 'Calls Duration',
        fr: 'Appels Durée',
        ar: 'مدة المكالمات',
      },

      // Product Details Page
      Back: { en: 'Back', fr: 'Retour', ar: 'رجوع' },
      Comments: { en: 'Comments', fr: 'Commentaires', ar: 'التعليقات' },
      DetailedReport: {
        en: 'Detailed Report',
        fr: 'Rapport détaillé',
        ar: 'تقرير مفصل',
      },
      SFE: { en: 'SFE', fr: 'SFE', ar: 'SFE' },
      VersionsCallsCharts: {
        en: 'Versions Calls Charts',
        fr: 'Graphiques des appels des versions',
        ar: 'مخططات مكالمات الإصدارات',
      },
      RepsCallsCharts: {
        en: 'Reps Calls Charts',
        fr: 'Graphiques des appels des représentants',
        ar: 'مخططات مكالمات المندوبين',
      },
      CallsDurations: {
        en: 'Calls Durations',
        fr: 'Durées des appels',
        ar: 'مدة المكالمات',
      },

      // Time Range Modal
      TimeRange: { en: 'Time Range', ar: 'النطاق الزمني', fr: 'Plage horaire' },
      From: { en: 'From', ar: 'من', fr: 'De' },
      To: { en: 'To', ar: 'إلى', fr: 'À' },
      Change: { en: 'Change', ar: 'تغيير', fr: 'Modifier' },
      Objection: { en: 'Objection', fr: 'Objection', ar: 'الاعتراض' },
      Remark: { en: 'Remark', fr: 'Remarque', ar: 'ملاحظة' },
      SelectStartDate: {
        en: 'Select start date',
        ar: 'اختر تاريخ البدء',
        fr: 'Sélectionner la date de début',
      },
      SelectEndDate: {
        en: 'Select end date',
        ar: 'اختر تاريخ الانتهاء',
        fr: 'Sélectionner la date de fin',
      },
      Minutes: { en: 'Minutes', fr: 'Minutes', ar: 'دقائق' },
      Snapshot: { en: 'Snapshot', fr: 'Instantané', ar: 'لقطة' },
      SlideNo: { en: 'Slide No.', fr: 'Diapositive N°', ar: 'شريحة رقم' },
      CloseModal: {
        en: 'Close modal',
        ar: 'إغلاق النافذة',
        fr: 'Fermer la fenêtre',
      },
      Calls: { en: 'Calls', fr: 'Appels', ar: 'المكالمات' },
      Download: { en: 'Download', fr: 'Télécharger', ar: 'تحميل' },
      Downloading: {
        en: 'Downloading...',
        fr: 'Téléchargement...',
        ar: 'جاري التحميل...',
      },
      SelectValue: {
        en: 'Select a value',
        fr: 'Sélectionner une valeur',
        ar: 'اختر قيمة',
      },
      Search: { en: 'Search', fr: 'Rechercher', ar: 'بحث' },
      Export: { en: 'Export', fr: 'Exporter', ar: 'تصدير' },
      SelectAll: {
        en: 'Select All',
        fr: 'Tout sélectionner',
        ar: 'تحديد الكل',
      },
      Doctor: { en: 'Doctor', fr: 'Médecin', ar: 'طبيب' },
      Provider: { en: 'Provider', fr: 'Prestataire', ar: 'مقدم خدمة' },
      Pharmacy: { en: 'Pharmacy', fr: 'Pharmacie', ar: 'صيدلية' },
      Hospital: { en: 'Hospital', fr: 'Hôpital', ar: 'مستشفى' },
      Retry: { en: 'Retry', fr: 'Réessayer', ar: 'إعادة المحاولة' },
      FailedToLoadData: {
        en: 'Failed to load data',
        fr: 'Échec du chargement des données',
        ar: 'فشل تحميل البيانات',
      },
      Confirmation: { en: 'Confirmation', fr: 'Confirmation', ar: 'تأكيد' },
      DeleteCustomerConfirmation: {
        en: 'Are you sure you want to delete the selected customer(s)?',
        fr: 'Êtes-vous sûr de vouloir supprimer le(s) client(s) sélectionné(s) ?',
        ar: 'هل أنت متأكد أنك تريد حذف العميل(العملاء) المحدد(المحددين)؟',
      },
      DeleteSingleCustomerConfirmation: {
        en: 'Are you sure you want to delete customer: ',
        fr: 'Êtes-vous sûr de vouloir supprimer le client : ',
        ar: 'هل أنت متأكد أنك تريد حذف العميل: ',
      },
      Loading: { en: 'Loading...', fr: 'Chargement...', ar: 'جاري التحميل...' },
      Cancel: { en: 'Cancel', fr: 'Annuler', ar: 'إلغاء' },
      Confirm: { en: 'Confirm', fr: 'Confirmer', ar: 'تأكيد' },
      SaveChanges: {
        en: 'Save Changes',
        fr: 'Enregistrer les modifications',
        ar: 'حفظ التغييرات',
      },

      Drafts: { en: 'Drafts', fr: 'Brouillons', ar: 'المسودات' },
      Creation: { en: 'Creation', fr: 'Création', ar: 'إنشاء' },
      Editing: { en: 'Editing', fr: 'Édition', ar: 'تحرير' },
      Deletion: { en: 'Deletion', fr: 'Suppression', ar: 'حذف' },
      Remove: { en: 'Remove', fr: 'Supprimer', ar: 'إزالة' },
      RemoveDraftConfirmation: {
        en: 'Are you sure you want to remove this item from the draft?',
        fr: 'Êtes-vous sûr de vouloir supprimer cet élément du brouillon ?',
        ar: 'هل أنت متأكد أنك تريد إزالة هذا العنصر من المسودة؟',
      },
      RemoveSingleDraftConfirmation: {
        en: 'Are you sure you want to remove from the draft:',
        fr: 'Êtes-vous sûr de vouloir supprimer du brouillon :',
        ar: 'هل أنت متأكد أنك تريد إزالة من المسودة:',
      },
      RemoveMultipleDraftsConfirmation: {
        en: 'Are you sure you want to remove the selected items from the draft?',
        fr: 'Êtes-vous sûr de vouloir supprimer les éléments sélectionnés du brouillon ?',
        ar: 'هل أنت متأكد أنك تريد إزالة العناصر المحددة من المسودة؟',
      },
      Approve: { en: 'Approve', fr: 'Approuver', ar: 'موافقة' },
      Reject: { en: 'Reject', fr: 'Rejeter', ar: 'رفض' },
      ApproveSingleDraftConfirmation: {
        en: 'Are you sure you want to approve:',
        fr: 'Êtes-vous sûr de vouloir approuver :',
        ar: 'هل أنت متأكد أنك تريد الموافقة على:',
      },
      ApproveMultipleDraftsConfirmation: {
        en: 'Are you sure you want to approve the selected items?',
        fr: 'Êtes-vous sûr de vouloir approuver les éléments sélectionnés ?',
        ar: 'هل أنت متأكد أنك تريد الموافقة على العناصر المحددة؟',
      },
      RejectSingleDraftConfirmation: {
        en: 'Are you sure you want to reject:',
        fr: 'Êtes-vous sûr de vouloir rejeter :',
        ar: 'هل أنت متأكد أنك تريد رفض:',
      },
      RejectMultipleDraftsConfirmation: {
        en: 'Are you sure you want to reject the selected items?',
        fr: 'Êtes-vous sûr de vouloir rejeter les éléments sélectionnés ?',
        ar: 'هل أنت متأكد أنك تريد رفض العناصر المحددة؟',
      },
      AcceptAll: { en: 'Accept All', fr: 'Tout accepter', ar: 'قبول الكل' },
      AcceptAllConfirmation: {
        en: 'Are you sure you want to accept all drafts?',
        fr: 'Êtes-vous sûr de vouloir accepter tous les brouillons ?',
        ar: 'هل أنت متأكد أنك تريد قبول جميع المسودات؟',
      },
      addDoctor: {
        en: 'Add Doctor',
        fr: 'Ajouter un médecin',
        ar: 'إضافة طبيب',
      },
      addProvider: {
        en: 'Add Provider',
        fr: 'Ajouter un prestataire',
        ar: 'إضافة مقدم خدمة',
      },
      itemsPerPage: {
        en: 'items per page',
        fr: 'articles par page',
        ar: 'عنصر في الصفحة',
      },
      addPharmacy: {
        en: 'Add Pharmacy',
        fr: 'Ajouter une pharmacie',
        ar: 'إضافة صيدلية',
      },
      addHospital: {
        en: 'Add Hospital',
        fr: 'Ajouter un hôpital',
        ar: 'إضافة مستشفى',
      },
      noPages: { en: 'No Pages', fr: 'Aucune page', ar: 'لا صفحات' },
      page: { en: 'Page', fr: 'Page', ar: 'صفحة' },
      pages: { en: 'Pages', fr: 'Pages', ar: 'صفحات' },
      // Report names
      'All Calls': {
        en: 'All Calls',
        fr: 'Tous les appels',
        ar: 'جميع المكالمات',
      },
      'All Visits': {
        en: 'All Visits',
        fr: 'Toutes les visites',
        ar: 'جميع الزيارات',
      },
      'Expired Visits': {
        en: 'Expired Visits',
        fr: 'Visites expirées',
        ar: 'الزيارات المنتهية',
      },
      'Visits Per Day': {
        en: 'Visits Per Day',
        fr: 'Visites par jour',
        ar: 'الزيارات في اليوم',
      },
      'Monthly coverage': {
        en: 'Monthly Coverage',
        fr: 'Couverture mensuelle',
        ar: 'التغطية الشهرية',
      },
      Classification: {
        en: 'Classification',
        fr: 'Classification',
        ar: 'التصنيف',
      },
      'Calls Locations': {
        en: 'Calls Locations',
        fr: 'Emplacements des appels',
        ar: 'مواقع المكالمات',
      },
      'Customers With Geo Location': {
        en: 'Customers With Geo Location',
        fr: 'Clients avec géolocalisation',
        ar: 'العملاء مع الموقع الجغرافي',
      },
      'Sales Reps Productivity': {
        en: 'Sales Reps Productivity',
        fr: 'Productivité des représentants',
        ar: 'إنتاجية المندوبين',
      },
      'Promoters Report': {
        en: 'Promoters Report',
        fr: 'Rapport des promoteurs',
        ar: 'تقرير المروجين',
      },
      'Supervisors KPIs': {
        en: 'Supervisors KPIs',
        fr: 'KPI des superviseurs',
        ar: 'مؤشرات المشرفين',
      },
      'Supervisors KPIs Expanded': {
        en: 'Supervisors KPIs Expanded',
        fr: 'KPI des superviseurs étendus',
        ar: 'مؤشرات المشرفين الموسعة',
      },
      'Call Rate/Targeted Call Rate': {
        en: 'Call Rate/Targeted Call Rate',
        fr: "Taux d'appel/Taux d'appel ciblé",
        ar: 'معدل المكالمات/معدل المكالمات المستهدف',
      },
      'Visit Rate/Targeted Visit Rate': {
        en: 'Visit Rate/Targeted Visit Rate',
        fr: 'Taux de visite/Taux de visite ciblé',
        ar: 'معدل الزيارات/معدل الزيارات المستهدف',
      },
      'Rep Average Call Duration': {
        en: 'Rep Average Call Duration',
        fr: 'Durée moyenne des appels',
        ar: 'متوسط مدة المكالمات',
      },
      'Double Visits': {
        en: 'Double Visits',
        fr: 'Visites doubles',
        ar: 'الزيارات المزدوجة',
      },
      'Supervisors Activities': {
        en: 'Supervisors Activities',
        fr: 'Activités des superviseurs',
        ar: 'أنشطة المشرفين',
      },
      'Managers Activities': {
        en: 'Managers Activities',
        fr: 'Activités des managers',
        ar: 'أنشطة المديرين',
      },
      'On Field Activities': {
        en: 'On Field Activities',
        fr: 'Activités sur le terrain',
        ar: 'الأنشطة الميدانية',
      },
      NFA: { en: 'NFA', fr: 'NFA', ar: 'NFA' },
      'Daily Activity': {
        en: 'Daily Activity',
        fr: 'Activité quotidienne',
        ar: 'النشاط اليومي',
      },
      'Rep Plans': {
        en: 'Rep Plans',
        fr: 'Plans des représentants',
        ar: 'خطط المندوبين',
      },
      'Supervisor Plans': {
        en: 'Supervisor Plans',
        fr: 'Plans des superviseurs',
        ar: 'خطط المشرفين',
      },
      'Plan Draft': {
        en: 'Plan Draft',
        fr: 'Brouillon de plan',
        ar: 'مسودة الخطة',
      },
      'Customers Plan Draft': {
        en: 'Customers Plan Draft',
        fr: 'Brouillon de plan clients',
        ar: 'مسودة خطة العملاء',
      },
      Orders: { en: 'Orders', fr: 'Orders', ar: 'الطلبات' },
      Expenses: { en: 'Expenses', fr: 'Expenses', ar: 'المصاريف' },
      Attendance: { en: 'Attendance', fr: 'Attendance', ar: 'الحضور' },
      'Target Frequency': {
        en: 'Dynamic Target Frequency',
        fr: 'Fréquence cible dynamique',
        ar: 'التردد المستهدف الديناميكي',
      },
      KPIs: { en: 'KPIs', fr: 'KPI', ar: 'مؤشرات الأداء' },
      NoDataFound: {
        en: 'No data found. Try adjusting your filters or selecting a different date range',
        fr: "Aucune donnée trouvée. Essayez d'ajuster vos filtres ou de sélectionner une plage de dates différente",
        ar: 'لم يتم العثور على بيانات. حاول ضبط المرشحات الخاصة بك أو تحديد نطاق تاريخ مختلف',
      },
      ErrorOccured: {
        en: 'An Error has occurred, please try again',
        fr: 'Une erreur est survenue, veuillez réessayer',
        ar: 'حدث خطأ، يرجى المحاولة مرة أخرى',
      },
      // Customer Add Actions
      CustomerAddedSuccess: {
        en: 'Customer added successfully',
        fr: 'Client ajouté avec succès',
        ar: 'تمت إضافة العميل بنجاح',
      },
      CustomersAddedSuccess: {
        en: 'Customers added successfully',
        fr: 'Clients ajoutés avec succès',
        ar: 'تمت إضافة العملاء بنجاح',
      },
      CustomerAddError: {
        en: 'Failed to add customer(s). Please try again.',
        fr: "Échec de l'ajout du/des client(s). Veuillez réessayer.",
        ar: 'فشل إضافة العميل (العملاء). يرجى المحاولة مرة أخرى.',
      },
      NoCustomersSelected: {
        en: 'No customers selected',
        fr: 'Aucun client sélectionné',
        ar: 'لم يتم تحديد أي عملاء',
      },
      TimeoutError: {
        en: 'Request timed out. Please try again.',
        fr: 'Le délai de la requête a expiré. Veuillez réessayer.',
        ar: 'انتهت مهلة الطلب. يرجى المحاولة مرة أخرى.',
      },
      SucessfullDeletion: {
        en: 'Customer(s) deleted successfully',
        fr: 'Client(s) supprimé(s) avec succès',
        ar: 'تم حذف العميل(العملاء) بنجاح',
      },
      SortingError: {
        en: 'Error in Sorting, please try again...',
        fr: 'Erreur de tri, veuillez réessayer...',
        ar: 'خطأ في الفرز، يرجى المحاولة مرة أخرى...',
      },
      FiltersError: {
        en: 'Error in Applying Filters, please try again...',
        fr: "Erreur lors de l'application des filtres, veuillez réessayer...",
        ar: 'خطأ في تطبيق المرشحات، يرجى المحاولة مرة أخرى...',
      },
      SupervisorsCannotEdit: {
        en: 'Supervisors cannot edit customers',
        fr: 'Les superviseurs ne peuvent pas modifier les clients',
        ar: 'لا يمكن للمشرفين تعديل العملاء',
      },
      DeletedCustomersCannotBeEdited: {
        en: 'Deleted customers cannot be edited',
        fr: 'Les clients supprimés ne peuvent pas être modifiés',
        ar: 'لا يمكن تعديل العملاء المحذوفين',
      },
      getAllDataError: {
        en: 'Error in fetching all data, please try again...',
        fr: 'Erreur lors de la récupération de toutes les données, veuillez réessayer...',
        ar: 'خطأ في جلب جميع البيانات، يرجى المحاولة مرة أخرى...',
      },
      ChartError: {
        en: 'Error in loading chart data, please try again...',
        fr: 'Erreur lors du chargement des données du graphique, veuillez réessayer...',
        ar: 'خطأ في تحميل بيانات المخطط، يرجى المحاولة مرة أخرى...',
      },
      PageSizeError: {
        en: 'Error in updating page size, please try again...',
        fr: 'Erreur lors de la mise à jour de la taille de la page, veuillez réessayer...',
        ar: 'خطأ في تحديث حجم الصفحة، يرجى المحاولة مرة أخرى...',
      },
      InitializeError: {
        en: 'Error in initializing table, please try again...',
        fr: "Erreur lors de l'initialisation du tableau, veuillez réessayer...",
        ar: 'خطأ في تهيئة الجدول، يرجى المحاولة مرة أخرى...',
      },
      UpdatePageError: {
        en: 'Error in updating page, please try again...',
        fr: 'Erreur lors de la mise à jour de la page, veuillez réessayer...',
        ar: 'خطأ في تحديث الصفحة، يرجى المحاولة مرة أخرى...',
      },
      // Customer Add Modal
      AddCustomer: {
        en: 'Add Customer',
        fr: 'Ajouter un client',
        ar: 'إضافة عميل',
      },
      EditCustomer: {
        en: 'Edit Customer',
        fr: 'Modifier le client',
        ar: 'تعديل العميل',
      },
      CustomerName: {
        en: 'Customer Name',
        fr: 'Nom du client',
        ar: 'اسم العميل',
      },
      Customer: {
        en: 'Customer',
        fr: 'Client',
        ar: 'العميل',
      },
      Rep: {
        en: 'Rep',
        fr: 'Représentant',
        ar: 'المندوب',
      },
      Activity: {
        en: 'Activity',
        fr: 'Activité',
        ar: 'النشاط',
      },
      ClassType: {
        en: 'Class',
        fr: 'Classe',
        ar: 'الفئة',
      },
      SelectClassType: {
        en: 'Select class',
        fr: 'Sélectionner la classe',
        ar: 'اختر الفئة',
      },
      MktClass: {
        en: 'MKT Class',
        fr: 'Classe MKT',
        ar: 'فئة السوق MKT',
      },
      SelectMktClass: {
        en: 'Select MKT class',
        fr: 'Sélectionner la classe MKT',
        ar: 'اختر فئة السوق MKT',
      },
      SpecialClass: {
        en: 'Special Class',
        fr: 'Classe spéciale',
        ar: 'الفئة الخاصة',
      },
      SelectSpecialClass: {
        en: 'Select special class',
        fr: 'Sélectionner la classe spéciale',
        ar: 'اختر الفئة الخاصة',
      },
      TargetedFrequency: {
        en: 'Targeted frequency (PM)',
        fr: 'Fréquence ciblée (PM)',
        ar: 'التردد المستهدف (PM)',
      },
      TargetedFrequencyMet: {
        en: 'Targeted Frequency Met',
        fr: 'Fréquence cible atteinte',
        ar: 'تم تحقيق التردد المستهدف',
      },
      EnterTargetedFrequency: {
        en: 'Enter targeted frequency',
        fr: 'Entrer la fréquence ciblée',
        ar: 'أدخل التردد المستهدف',
      },
      InvalidEmailWithExample: {
        en: 'Please enter a valid username. Example: name@company',
        fr: 'Veuillez saisir un username valide. Exemple : name@company',
        ar: 'يرجى إدخال اسم مستخدم صالح. مثال: name@company',
      },
      InvalidTargetedFrequency: {
        en: 'Targeted frequency must be at least 0.',
        fr: 'La fréquence ciblée doit être au moins 0.',
        ar: 'يجب أن يكون التردد المستهدف 0 على الأقل.',
      },
      Required: {
        en: 'This field is required.',
        fr: 'Ce champ est obligatoire.',
        ar: 'هذا الحقل مطلوب.',
      },
      Add: {
        en: 'Add',
        fr: 'Ajouter',
        ar: 'إضافة',
      },
      MyLeaves: {
        en: 'My Leaves',
        fr: 'Mes Congés',
        ar: 'إجازاتي',
      },
      Overview: {
        en: 'Overview',
        fr: 'Aperçu',
        ar: 'نظرة عامة',
      },
      GrantLeaves: {
        en: 'Grant Leaves',
        fr: 'Accorder des Congés',
        ar: 'منح الإجازات',
      },
      LeavesGrantedSuccess: {
        en: 'Leaves granted successfully',
        fr: 'Congés accordés avec succès',
        ar: 'تم منح الإجازات بنجاح',
      },
      LeavesGrantFailed: {
        en: 'Failed to grant leaves. Please try again.',
        fr: "Échec de l'attribution des congés. Veuillez réessayer.",
        ar: 'فشل منح الإجازات. الرجاء المحاولة مرة أخرى.',
      },
      NetworkError: {
        en: 'Network error, please try again',
        fr: 'Erreur réseau, veuillez réessayer',
        ar: 'خطأ في الشبكة، الرجاء المحاولة مرة أخرى',
      },
      PendingLeaves: {
        en: 'Pending Leaves',
        fr: 'Congés en Attente',
        ar: 'الإجازات المعلقة',
      },
      Activities: {
        en: 'Activities',
        fr: 'Activités',
        ar: 'الأنشطة',
      },
      Status: {
        en: 'Status',
        fr: 'Statut',
        ar: 'الحالة',
      },
      Duration: {
        en: 'Duration (mins)',
        fr: 'Durée (mins)',
        ar: 'المدة (دقائق)',
      },
      Accepted: {
        en: 'Accepted',
        fr: 'Accepté',
        ar: 'مقبول',
      },
      Rejected: {
        en: 'Rejected',
        fr: 'Rejeté',
        ar: 'مرفوض',
      },
      Pending: {
        en: 'Pending',
        fr: 'En attente',
        ar: 'قيد الانتظار',
      },
      UnableToOpenUserLeavesOverview: {
        en: 'Unable to open user leaves overview',
        fr: "Impossible d'ouvrir l'aperçu des congés de l'utilisateur",
        ar: 'تعذر فتح نظرة عامة على إجازات المستخدم',
      },
      ErrorFetchingActivities: {
        en: 'Error fetching activities',
        fr: 'Erreur lors de la récupération des activités',
        ar: 'حدث خطأ أثناء جلب الأنشطة',
      },
      UsersCategories: {
        en: 'Users Categories',
        fr: "Catégories d'Utilisateurs",
        ar: 'فئات المستخدمين',
      },
      Type: {
        en: 'Type',
        fr: 'Type',
        ar: 'النوع',
      },
      Subtype: {
        en: 'SubType',
        fr: 'Sous-type',
        ar: 'النوع الفرعي',
      },
      IsNonFieldActivity: {
        en: 'Is Non-Field Activity?',
        fr: 'Est une activité hors terrain?',
        ar: 'هل هي نشاط غير ميداني؟',
      },
      UsersIds: {
        en: 'Users Ids',
        fr: 'Identifiants des utilisateurs',
        ar: 'معرفات المستخدمين',
      },
      ChooseToDate: {
        en: 'Choose To Date',
        fr: 'Choisir la date de fin',
        ar: 'اختر تاريخ الانتهاء',
      },
      Submit: {
        en: 'Submit',
        fr: 'Soumettre',
        ar: 'إرسال',
      },
      SelectUser: {
        en: 'Select User',
        fr: "Sélectionner l'Utilisateur",
        ar: 'اختر المستخدم',
      },

      UsersMissing: {
        en: 'Users are required.',
        fr: 'Les utilisateurs sont requis.',
        ar: 'المستخدمون مطلوبون.',
      },
      TypeMissing: {
        en: 'Type is required.',
        fr: 'Le type est requis.',
        ar: 'النوع مطلوب.',
      },
      FromMissing: {
        en: 'From date is required.',
        fr: 'La date de début est requise.',
        ar: 'تاريخ البدء مطلوب.',
      },
      ToMissing: {
        en: 'To date is required.',
        fr: 'La date de fin est requise.',
        ar: 'تاريخ الانتهاء مطلوب.',
      },
      SubtypeMissing: {
        en: 'Subtype is required.',
        fr: 'Le sous-type est requis.',
        ar: 'النوع الفرعي مطلوب.',
      },
      AvailableLeavesBalance: {
        en: 'Available Leaves Balance',
        fr: 'Solde des congés disponibles',
        ar: 'رصيد الإجازات المتاحة',
      },
      EmergencyLeavesBalance: {
        en: 'Emergency Leaves Balance',
        fr: "Solde des congés d'urgence",
        ar: 'رصيد إجازات الطوارئ',
      },
      Annual: {
        en: 'Annual',
        fr: 'Annuel',
        ar: 'سنوي',
      },
      Shifted: {
        en: 'Shifted',
        fr: 'Reporté',
        ar: 'منقول',
      },
      userCategoriesTitle: {
        en: 'Excel sheet to set categories for multiple users',
        fr: 'Feuille Excel pour définir des catégories pour plusieurs utilisateurs',
        ar: 'ورقة إكسل لتعيين فئات لعدة مستخدمين',
      },
      userCategoriesInstructions: {
        en: `  The first row columns are always "Name" and "Category" and Each row represents
                one user?. Columns should be only added in this repctive order: name,
                category.`,
        fr: `  Les colonnes de la première ligne sont toujours "Nom" et "Catégorie" et chaque ligne représente
                un utilisateur?. Les colonnes ne doivent être ajoutées que dans cet ordre respectif : nom,
                catégorie.`,
        ar: `  أعمدة الصف الأول هي دائمًا "الاسم" و "الفئة" ويمثل كل صف
            مستخدم واحد؟. يجب إضافة الأعمدة بهذا الترتيب فقط: الاسم،
            الفئة.`,
      },
      userCategoriesNote: {
        en: `Note that values are case sensetive`,
        fr: `Notez que les valeurs sont sensibles à la casse`,
        ar: `لاحظ أن القيم حساسة لحالة الأحرف`,
      },
      UploadFile: {
        en: 'Upload File',
        fr: 'Télécharger le fichier',
        ar: 'رفع الملف',
      },
      DownloadTemplate: {
        en: 'Download Template',
        fr: 'Télécharger le modèle',
        ar: 'تحميل القالب',
      },
      NoFileChosen: {
        en: 'No file chosen',
        fr: 'Aucun fichier choisi',
        ar: 'لم يتم اختيار ملف',
      },
      name: {
        en: 'name',
        fr: 'nom',
        ar: 'الاسم',
      },
      SelectAnOption: {
        en: 'Select an option',
        fr: 'Sélectionner une option',
        ar: 'اختر خيارا',
      },

      Login: { en: 'Login', fr: 'Connexion', ar: 'سجل الدخول' },
      LoginUsername: {
        en: 'Username',
        fr: "Nom d'utilisateur",
        ar: 'اسم المستخدم',
      },
      Password: { en: 'Password', fr: 'Mot de passe', ar: 'كلمة المرور' },
      'Username-required': {
        en: 'Username is required',
        fr: "LLe nom d'utilisateur est requis",
        ar: 'اسم المستخدم مطلوب',
      },
      'Username-format-invalid': {
        en: 'Enter a valid username. Example: name@company',
        fr: 'Entrez un username valide Exemple : name@company',
        ar: 'أدخل اسم مستخدم صالحًا مثال: name@company',
      },
      'Password-required': {
        en: 'Password is required',
        fr: 'Le mot de passe est requis',
        ar: 'كلمة المرور مطلوبة',
      },
      'Enter-Email': {
        en: 'Enter Email',
        fr: "Entrez l'e-mail",
        ar: 'أدخل البريد الإلكتروني',
      },
      EnterUsername: {
        en: 'Enter Username',
        fr: "Entrez le nom d'utilisateur",
        ar: 'أدخل اسم المستخدم',
      },
      'Enter-Password': {
        en: 'Enter Password',
        fr: 'Entrez le mot de passe',
        ar: 'أدخل كلمة المرور',
      },
      Cities: {
        en: 'Cities',
        fr: 'Villes',
        ar: 'المدن',
      },
      Countries: {
        en: 'Countries',
        fr: 'Pays',
        ar: 'الدول',
      },
      Provinces: {
        en: 'Provinces',
        fr: 'Provinces',
        ar: 'المقاطعات',
      },

      // Customer Creation Form Fields
      CustomerType: {
        en: 'Customer Type',
        fr: 'Type de client',
        ar: 'نوع العميل',
      },
      doctor: { en: 'Doctor', fr: 'Médecin', ar: 'طبيب' },
      provider: { en: 'Provider', fr: 'Prestataire', ar: 'مقدم خدمة' },
      pharmacy: { en: 'Pharmacy', fr: 'Pharmacie', ar: 'صيدلية' },
      hospital: { en: 'Hospital', fr: 'Hôpital', ar: 'مستشفى' },
      Name: { en: 'Name', fr: 'Nom', ar: 'الاسم' },
      EnterName: {
        en: 'Enter name',
        fr: 'Entrer le nom',
        ar: 'أدخل الاسم',
      },
      EnterEmail: {
        en: 'Enter email',
        fr: "Entrer l'email",
        ar: 'أدخل البريد الإلكتروني',
      },
      Phone: { en: 'Phone', fr: 'Téléphone', ar: 'الهاتف' },
      EnterPhone: {
        en: 'Enter phone',
        fr: 'Entrer le téléphone',
        ar: 'أدخل الهاتف',
      },
      Country: { en: 'Country', fr: 'Pays', ar: 'البلد' },
      SelectCountry: {
        en: 'Select country',
        fr: 'Sélectionner le pays',
        ar: 'اختر البلد',
      },
      Province: { en: 'Province', fr: 'Province', ar: 'المحافظة' },
      SelectProvince: {
        en: 'Select province',
        fr: 'Sélectionner la province',
        ar: 'اختر المحافظة',
      },
      City: { en: 'City', fr: 'Ville', ar: 'المدينة' },
      EnterCity: {
        en: 'Enter city',
        fr: 'Entrer la ville',
        ar: 'أدخل المدينة',
      },
      Address: { en: 'Address', fr: 'Adresse', ar: 'العنوان' },
      EnterAddress: {
        en: 'Enter address',
        fr: "Entrer l'adresse",
        ar: 'أدخل العنوان',
      },
      Speciality: { en: 'Speciality', fr: 'Spécialité', ar: 'التخصص' },
      SelectSpeciality: {
        en: 'Select speciality',
        fr: 'Sélectionner la spécialité',
        ar: 'اختر التخصص',
      },
      Center: { en: 'Center', fr: 'Centre', ar: 'المركز' },
      EnterCenter: {
        en: 'Enter center',
        fr: 'Entrer le centre',
        ar: 'أدخل المركز',
      },
      PharmacyLicense: {
        en: 'Pharmacy License',
        fr: 'Licence de pharmacie',
        ar: 'ترخيص الصيدلية',
      },
      EnterLicense: {
        en: 'Enter license number',
        fr: 'Entrer le numéro de licence',
        ar: 'أدخل رقم الترخيص',
      },
      HospitalType: {
        en: 'Hospital Type',
        fr: "Type d'hôpital",
        ar: 'نوع المستشفى',
      },
      SelectHospitalType: {
        en: 'Select hospital type',
        fr: "Sélectionner le type d'hôpital",
        ar: 'اختر نوع المستشفى',
      },
      Department: { en: 'Department', fr: 'Département', ar: 'القسم' },
      EnterDepartment: {
        en: 'Enter department',
        fr: 'Entrer le département',
        ar: 'أدخل القسم',
      },
      SelectCity: {
        en: 'Select city',
        fr: 'Sélectionner la ville',
        ar: 'اختر المدينة',
      },
      EnterDoctorName: {
        en: 'Enter doctor name',
        fr: 'Entrer le nom du médecin',
        ar: 'أدخل اسم الطبيب',
      },
      Doctors: {
        en: 'Doctors',
        fr: 'Médecins',
        ar: 'الأطباء',
      },
      AtLeastOneItem: {
        en: 'at least one item',
        fr: 'au moins un élément',
        ar: 'عنصر واحد على الأقل',
      },
      CustomerCreatedSuccess: {
        en: 'Customer draft created successfully',
        fr: 'Brouillon de client créé avec succès',
        ar: 'تم إنشاء مسودة العميل بنجاح',
      },
      CustomerCreateError: {
        en: 'Failed to create customer draft. Please try again.',
        fr: 'Échec de création du brouillon de client. Veuillez réessayer.',
        ar: 'فشل إنشاء مسودة العميل. يرجى المحاولة مرة أخرى.',
      },
      products: { en: 'Products', fr: 'Produits', ar: 'المنتجات' },
      Evaluation: { en: 'Evaluation', fr: 'Évaluation', ar: 'التقييم' },
      SelectedUsers: {
        en: 'Selected Users',
        fr: 'Utilisateurs sélectionnés',
        ar: 'المستخدمون المختارون',
      },
      ExportAll: { en: 'Export All', fr: 'Tout exporter', ar: 'تصدير الكل' },
      AverageScores: {
        en: 'Average Scores',
        fr: 'Scores moyens',
        ar: 'متوسط ​​الدرجات',
      },
      EvaluationError: {
        en: 'Failed to fetch evaluation',
        fr: "Impossible de récupérer l'évaluation",
        ar: 'فشل في جلب التقييم',
      },
      MembersListError: {
        en: 'Failed to fetch team members',
        fr: "Impossible de récupérer les membres de l'équipe",
        ar: 'فشل في جلب أعضاء الفريق',
      },

      // Users Page
      MainKPIs: {
        en: 'Main KPIs',
        fr: 'KPI Principaux',
        ar: 'مؤشرات الأداء الرئيسية',
      },
      TotalCoverage: {
        en: 'Total Coverage',
        fr: 'Couverture Totale',
        ar: 'التغطية الإجمالية',
      },
      PrivateMarket: {
        en: 'Private Market',
        fr: 'Marché Privé',
        ar: 'السوق الخاص',
      },
      Pharmacies: { en: 'Pharmacies', fr: 'Pharmacies', ar: 'الصيدليات' },
      Hospitals: { en: 'Hospitals', fr: 'Hôpitaux', ar: 'المستشفيات' },
      ClassesDistribution: {
        en: 'Classes Distribution',
        fr: 'Distribution des Classes',
        ar: 'توزيع الفئات',
      },
      MarketSamples: {
        en: 'Market Samples',
        fr: 'Échantillons du Marché',
        ar: 'عينات السوق',
      },
      ProductSamples: {
        en: 'Product Samples',
        fr: 'Échantillons de Produits',
        ar: 'عينات المنتجات',
      },
      AccumulativeList: {
        en: 'Accumulative List',
        fr: 'Liste Cumulative',
        ar: 'القائمة التراكمية',
      },
      Count: { en: 'Count', fr: 'Nombre', ar: 'العدد' },
      Specialities: { en: 'Specialities', fr: 'Spécialités', ar: 'التخصصات' },
      Covered: { en: 'Covered', fr: 'Couvert', ar: 'مغطى' },
      Met: { en: 'Met', fr: 'Atteint', ar: 'محقق' },
      NotMet: { en: 'Not Met', fr: 'Non atteint', ar: 'غير محقق' },
      Uncovered: { en: 'Uncovered', fr: 'Non Couvert', ar: 'غير مغطى' },
      DownloadCoveredReport: {
        en: 'Covered Report',
        fr: 'Rapport des clients couverts',
        ar: 'تقرير العملاء المغطين',
      },
      DownloadUncoveredReport: {
        en: 'Uncovered Report',
        fr: 'Rapport des clients non couverts',
        ar: 'تقرير العملاء غير المغطين',
      },
      CustomerVisits: {
        en: 'Customer Visits',
        fr: 'Visites Clients',
        ar: 'زيارات العملاء',
      },
      VisitsLegend: {
        en: 'Visits legend',
        fr: 'Légende des visites',
        ar: 'وسيلة إيضاح الزيارات',
      },
      'Single Visit': {
        en: 'Single Visit',
        fr: 'Visite simple',
        ar: 'زيارة مفردة',
      },
      'Double Visit': {
        en: 'Double Visit',
        fr: 'Visite double',
        ar: 'زيارة مزدوجة',
      },
      'Triple Visit': {
        en: 'Triple Visit',
        fr: 'Visite triple',
        ar: 'زيارة ثلاثية',
      },
      Workshop: {
        en: 'Workshop',
        fr: 'Atelier',
        ar: 'ورشة عمل',
      },
      MonthlyReport: {
        en: 'Monthly Report',
        fr: 'Rapport Mensuel',
        ar: 'التقرير الشهري',
      },
      Planner: {
        en: 'Planner',
        fr: 'Planificateur',
        ar: 'المخطط',
      },
      CallsLocations: {
        en: 'Calls Locations',
        fr: 'Emplacements des Appels',
        ar: 'مواقع المكالمات',
      },
      ListDistribution: {
        en: 'List Distribution',
        fr: 'Distribution de Liste',
        ar: 'توزيع القائمة',
      },
      ClassDistribution: {
        en: 'Class Distribution',
        fr: 'Distribution des Classes',
        ar: 'توزيع الفئات',
      },
      PlannedVisit: {
        en: 'Planned Visit',
        fr: 'Visite Planifiée',
        ar: 'زيارة مخططة',
      },
      UnplannedVisit: {
        en: 'Unplanned Visit',
        fr: 'Visite Non Planifiée',
        ar: 'زيارة غير مخططة',
      },
      Unvisited: {
        en: 'Unvisited',
        fr: 'Non Visité',
        ar: 'لم تتم الزيارة',
      },
    };
    this.dictionary = {
      ...this.dictionary,
      ...ticketTranslations,
    };
  }

  /**
   * Get a translation for the current language.
   */
  get(key: string): string {
    return this.dictionary[key]?.[this.language] ?? key;
  }

  /**
   * Change the active language at runtime.
   */
  setLanguage(lang: language): void {
    if (lang !== this.language) {
      this.loadingService.showLoader();

      this.language = lang;

      window.localStorage.setItem('language', lang);

      window.location.reload();
    }
  }
}
